import React, { useEffect, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { heightMotion } from '@/src/renderer/lib/motion/heightMotion'
import { useContentHeight } from '@/src/renderer/hooks/useContentHeight'
import type { AutoHeightProps } from './AutoHeight.types'

/**
 * **Eine Hülle, die ihre Höhe fährt statt sie springen zu lassen.**
 *
 * Sie kann zweierlei, weil beides dieselbe Frage ist:
 *
 * - **Im Stand**: Der Inhalt wird länger oder kürzer, die Hülle fährt mit. Dafür gibt es
 *   sonst nichts — `height: 'auto'` löst hier keinen Übergang aus, weil das ZIEL `auto`
 *   bleibt und sich nur der Inhalt ändert. Deshalb wird gemessen.
 * - **Als Kind von `AnimatePresence`**: Sie bringt `initial` und `exit` mit und fährt auch
 *   auf und zu.
 *
 * Für Elemente, die in einer REIHE Platz machen und wieder verschwinden, ist
 * `growMotion` (`lib/motion/`) das Richtige: Dort ist das Maß beim Erscheinen 0 und am
 * Ende `auto`, und Inhalt und Maß sind gegeneinander gestaffelt.
 *
 * **Ohne Messung bleibt es bei `auto` und ohne Bewegung** — ein Tab hinter `display: none`
 * misst nur Nullen (CLAUDE.md, TabStack-Falle), und in jsdom hat nichts Maße. Der Inhalt
 * steht dann sofort und vollständig da; geklemmt wird er nie.
 *
 * @example
 * // Der Inhalt wechselt, die Höhe fährt mit:
 * <AutoHeight>{mode === 'week' ? <Weekdays /> : <Interval />}</AutoHeight>
 *
 * @example
 * // Auf und zu, in einer Flex-Spalte mit 16px Abstand:
 * <AnimatePresence initial={false}>
 *   {open && <AutoHeight collapseGap={16}><Builder /></AutoHeight>}
 * </AnimatePresence>
 */
export const AutoHeight: React.FC<AutoHeightProps> = ({
  children, collapseGap = 0, animated = true, watchRest = true, appear = false, instantFollow = false, duration, className, bodyClassName,
}) => {
  const reducedMotion = useReducedMotion()
  const moves = animated && !reducedMotion
  const [bodyRef, height] = useContentHeight(moves, watchRest)
  const { initial, animate, exit } = heightMotion({ height, collapseGap, duration, reduced: !moves })

  /*
    `instantFollow`: Erst muss das Erscheinen gelaufen sein — es fährt mit derselben Übergabe wie
    jeder Wechsel. Danach folgt die Hülle im Stand sofort. Gezählt wird die Zeit, nicht das Ende
    der Bewegung: Steht die Hülle in einem `AnimatePresence initial={false}`, läuft beim Aufbau gar
    keine, und auf ihr Ende wartete man vergeblich.
  */
  const [entered, setEntered] = useState(!appear)
  useEffect(() => {
    if (entered || !instantFollow) return
    const timer = window.setTimeout(() => setEntered(true), (duration ?? 0.26) * 1000)
    return () => window.clearTimeout(timer)
  }, [entered, instantFollow, duration])
  const target = instantFollow && entered ? { ...animate, transition: { duration: 0 } } : animate

  return (
    <motion.div
      className={className}
      // `clip` statt `hidden`: Während die Höhe läuft, darf nichts herausstehen. Die
      // Zugabe lässt im Ruhezustand Fokusringe und Schatten heraus, die `hidden` glatt
      // abschnitte.
      style={{ overflow: 'clip', overflowClipMargin: 4 }}
      // `false` heißt bei Framer „steh gleich im Zielzustand" — kein Auffahren beim
      // ersten Aufbau. `exit` bleibt davon unberührt.
      initial={appear ? initial : false}
      animate={target}
      exit={exit}
    >
      <div className={bodyClassName} ref={bodyRef}>{children}</div>
    </motion.div>
  )
}
